/* global window, document */

import videojs from 'video.js';
import ProgressBar from 'progressbar.js';
import isMobileJS from 'ismobilejs';

import { resizePoster } from './utils';

const nextVideoWaitTime = 5000;

/**
 * Sets the recommendations overlay.
 * @param {Object} Encore - Encore video player instance.
 * @returns {Object} overlay progress bar.
 */
function setupUpNextOverlay(encore) {
  encore.player.dock({
    title: 'Up Next',
  });

  const countdownEl = document.createElement('div');
  countdownEl.classList.add('vjs-overlay-progress');
  countdownEl.setAttribute('id', `next-countdown-${encore.player.id()}`);

  const divEl = document.createElement('div');
  divEl.appendChild(countdownEl);

  const titleEl = document.createElement('p');
  titleEl.classList.add('vjs-overlay-title');
  titleEl.innerHTML = encore.source.title;

  const imgEl = document.createElement('img');
  imgEl.classList.add('vjs-overlay-image');
  imgEl.setAttribute('src', resizePoster(encore.source.poster, 320, 50));
  imgEl.addEventListener('click', () => {
    encore.recTracker.setSkipWaitClicked(true);
  });
  divEl.appendChild(imgEl);
  divEl.appendChild(titleEl);

  encore.player.overlay({
    overlays: [
      {
        content: divEl,
        align: 'center',
        start: 'show-overlay',
        end: 'hide-overlay',
      },
    ],
  });

  const progressId = `#next-countdown-${encore.player.id()}`;
  const progressBar = new ProgressBar.Line(progressId, {
    strokeWidth: 4,
    color: '#2483d5',
  });
  progressBar.set(0);

  return progressBar;
}

/**
 * Overlay wait callback function.
 * Hides overlay, restores player controls, sets new dock title,
 * restores play button, sets player source and requests a new ad.
 * @param {Object} Encore - Encore video player instance.
 */
function waitCallback(encore) {
  encore.player.dock({});
  encore.player.trigger('hide-overlay');
  encore.player.trigger('recommendationLoaded');

  if (!isMobileJS.any) {
    encore.player.reset();
  }

  encore.bid.request()
    .then(() => {
      encore.setPlayerAttributes(encore.player);
      encore.player.autoplay(true);
      encore.player.bigPlayButton.show();

      encore.setPlayerSrc(encore.player);
      encore.player.trigger('ads-ima-adRequest');
      encore.player.ima.requestAds();

      if (encore.initialEvent === 'touchend' && !isMobileJS.any) {
        videojs.log(`${encore.playerElementId} isMobile = [${isMobileJS.any}] initialEvent = [${encore.initialEvent}]`);
        setTimeout(() => {
          encore.player.play()
            .then(result => videojs.log(result))
            .catch(error => videojs.log.error(error));
        }, 1000);
      }
    });
}

/**
 * Overlay wait function.
 * @param {integer} startTime - overlay start wait time.
 * @param {Object} Encore - Encore video player instance.
 * @param {Object} progressBar - overlay progress bar.
 */
function waitFunc(startTime, encore, progressBar) {
  const ellapsedTime = Date.now() - startTime;
  if (ellapsedTime > nextVideoWaitTime) {
    progressBar.destroy();
    waitCallback(encore);
    return;
  }

  progressBar.animate(ellapsedTime / nextVideoWaitTime, {
    duration: 100,
  });

  setTimeout(waitFunc.bind(this, startTime, encore, progressBar), 125);
}

/**
 * Set next video recommendation as player source.
 * @param {Object} Encore - Encore video player instance.
 */
function setNextSource(encore) {
  encore.recTracker.setRecWatched(encore.source.id);

  if (!encore.recTracker.hasRecommendations) {
    return;
  }

  const nextSource = encore.recTracker.getNextRec();
  if (!nextSource) {
    return;
  }

  videojs.log(`${encore.playerElementId} Set next source [${nextSource.id}]`);

  nextSource.setIndex(encore.recTracker.index);
  nextSource.setIsSticky(encore.source.isSticky);
  encore.setSource(nextSource);

  if (encore.source.isSticky) {
    waitCallback(encore);
  } else {
    encore.player.controls(false);
    encore.player.poster('');

    const progressBar = setupUpNextOverlay(encore);

    encore.player.trigger('show-overlay');
    if (encore.player.bigPlayButton) {
      encore.player.bigPlayButton.hide();
    }

    const startTime = Date.now();

    waitFunc(startTime, encore, progressBar);
  }
}

/**
 * Attach video recommendations events to video player.
 * @param {Object} Encore - Encore video player instance.
 */
function attachRecommendationEvents(encore) {
  const { imaSdkAvailable, adBlockDetected } = encore.settings;
  if (encore.source.isPlaylist ||
    !encore.source.requiresRecommendations ||
    !imaSdkAvailable ||
    adBlockDetected) {
    return;
  }

  if (window.hearstPlayer.loadRecommendations) {
    encore.player.on('played-q0', () => {
      videojs.log(`${encore.playerElementId} Loading recommendations`);
      window.hearstPlayer.loadRecommendations();
    });
  }

  encore.player.on('contentended', setNextSource.bind(this, encore));
}

export default attachRecommendationEvents;
